'use client';

import NextImage from 'next/image';
import Icon from '@/app/components/ui/common/icon-component';
import TopImage from './top-image';
import React, { forwardRef, Ref } from 'react';
import { JContainer } from '../../ui/layout';
import { cn } from '@/app/lib/utils/utils';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

interface TopContainerRefs {
    backgroundRef: HTMLDivElement | null;
    topImageRef: HTMLDivElement | null;
}

interface TopContainerProps {
    backgroundColor: string;
    backgroundImage: string;
    title?: string;
    titleIcon?: string | null;
    mainImage?: {
        default: string;
        small?: string;
    };
    children: React.ReactNode;
}

export default forwardRef<TopContainerRefs, TopContainerProps>(
    function TopContainer(
        {
            backgroundColor,
            backgroundImage,
            title = '',
            titleIcon = null,
            mainImage,
            children,
        },
        ref: Ref<TopContainerRefs>,
    ) {
        const backgroundRef = React.useRef<HTMLDivElement>(null);
        const topImageRef = React.useRef<HTMLDivElement>(null);

        // 暴露内部refs给父组件
        React.useImperativeHandle(
            ref,
            () => ({
                backgroundRef: backgroundRef.current,
                topImageRef: topImageRef.current,
            }),
            [],
        ); // 添加空依赖数组，确保只在组件挂载时执行一次

        return (
            <JContainer
                size="full"
                padding="none"
                className="relative flex flex-col items-center justify-center overflow-hidden"
                style={{ backgroundColor }}
            >
                <div
                    ref={backgroundRef}
                    className="absolute left-0 top-0 z-0 h-full w-full"
                >
                    <NextImage
                        src={getStaticAssetUrl(backgroundImage)}
                        alt="net-bg"
                        fill
                        priority
                        draggable={false}
                        className="pointer-events-none !h-auto !w-full select-none object-top"
                    />
                </div>
                <div
                    className={`z-10 flex w-full flex-col items-center justify-center`}
                >
                    <div
                        ref={topImageRef}
                        className={cn(
                            'flex w-full flex-col items-center justify-center',
                            {
                                'tablet:py-[60px] desktop:py-[80px] py-[40px]':
                                    !title || !titleIcon,
                                'tablet:pt-[60px] desktop:pt-[80px] tablet:pb-[24px] desktop:pb-[32px] pb-[16px] pt-[40px]':
                                    title && titleIcon,
                            },
                        )}
                    >
                        {(title || titleIcon) && (
                            <div className="tablet:pb-[16px] desktop:pb-[16px] flex items-center gap-1 pb-[12px]">
                                {titleIcon && (
                                    <Icon
                                        name={titleIcon}
                                        className={cn(
                                            'inline-block',
                                            'tablet:w-[21px] desktop:w-[21px] w-[19px]',
                                            'tablet:h-[21px] desktop:h-[21px] h-[19px]',
                                        )}
                                    />
                                )}
                                <h1 className="j-l1 text-[#0A0708]">{title}</h1>
                            </div>
                        )}

                        {mainImage && (
                            <TopImage
                                defaultImage={mainImage.default}
                                smallImage={
                                    mainImage.small ?? mainImage.default
                                }
                            />
                        )}
                    </div>
                    <div className="tablet:pb-[80px] desktop:pb-[100px] flex w-full flex-col items-center pb-[60px]">
                        {children}
                    </div>
                </div>
            </JContainer>
        );
    },
);
